/**
 * What has happened while this customer was being set up.
 *
 * Every line has been written since the day its feature was built and none of
 * it could be read back: every other reader of an organization's audit log is
 * scoped to the caller's own organization, and a platform system administrator
 * has none. So nobody could answer "has the BAA been sent, and by whom" without
 * opening DocuSign, and an onboarding handed from one person to another started
 * from nothing.
 *
 * The sentences come from the backend. A timeline that re-words events in the
 * client is one that disagrees with the log it claims to show.
 */
import { useQuery } from '@tanstack/react-query';
import { call } from '../../../../lib/callable';
import { Spinner } from '../../../../app/Spinner';
import page from '../../../../shared/Page.module.css';
import own from '../Onboarding.module.css';

interface Entry {
  id: string; action: string; stage: string | null;
  what: string; who: string; at: string | null;
}

const day = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : null);

export function ActivityView({ orgId }: { orgId: string }) {
  const activity = useQuery({
    queryKey: ['onboarding-activity', orgId],
    queryFn: () => call<{ orgId: string }, { entries: Entry[]; more: boolean }>(
      'sysAdminGetOrgActivity', { orgId },
    ),
    retry: false,
  });

  if (activity.isLoading) return <Spinner label="Reading the history" />;
  if (activity.error) {
    return (
      <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
        Could not read this organization&apos;s history.
      </div>
    );
  }

  const entries = activity.data?.entries ?? [];
  if (entries.length === 0) {
    return <p className={page.absent}>Nothing recorded yet.</p>;
  }

  return (
    <>
      <p className={page.itemMeta}>
        Setting this customer up, newest first. Their own staff&apos;s activity — policies,
        approvals, acknowledgements — is theirs and is not here.
      </p>
      <ul className={own.timeline} aria-label="Onboarding history">
        {entries.map((entry) => (
          <li key={entry.id} className={own.timelineRow}>
            <div className={page.itemName}>{entry.what}</div>
            <div className={page.itemMeta}>
              {day(entry.at) ?? 'Undated'} · {entry.who}
              {entry.stage ? ` · ${entry.stage}` : ''}
            </div>
          </li>
        ))}
      </ul>
      {activity.data?.more && (
        <p className={page.itemMeta}>Older entries exist and are not shown.</p>
      )}
    </>
  );
}
